import {
  isDonationTicketDefinition,
  hasPricingOptions,
  isPricingOptionsWithSamePrice,
} from '@wix/wix-events-commons-statics'
import React from 'react'
import {useTranslation} from '@wix/yoshi-flow-editor'
import {TicketWithFormattedSalePeriod} from '@wix/events-types'
import {DONATION_ERROR} from '../../../../../types'
import {Donation} from '../donation'
import {checkDonationError} from '../../../../../services/donations'
import {getPricingOptionsRangeLabel} from '../../../../../selectors/seating/place'
import {useCurrencyFormatter} from '../../../../../../../../commons/hooks/currency'
import {getSelectedTicketDonation, getSelectedTicketDonationError} from '../../../../../selectors/selected-tickets'
import {usePageActions, usePageStateSelector} from '../../../../../hooks/state'
import {isDonationInputVisible} from '../../../../../selectors/tickets-picker'
import {Fees} from '../fees'
import {Price} from '../price'
import {GoesOnSaleLabel} from '../goes-on-sale-label'
import {OnSaleLabel} from '../on-sale-label'
import s from './ticket-price.scss'

interface TicketPriceProps {
  ticket: TicketWithFormattedSalePeriod
  className?: string
}

export const TicketPrice = ({className, ticket}: TicketPriceProps) => {
  const {t} = useTranslation()
  const {getFormattedMoney} = useCurrencyFormatter()

  const donation = usePageStateSelector(state => getSelectedTicketDonation(state, ticket.id))
  const donationError = usePageStateSelector(state => getSelectedTicketDonationError(state, ticket.id))
  const donationInputVisible = usePageStateSelector(state => isDonationInputVisible(state, ticket))
  const {changeTicketDonation, setDonationError} = usePageActions()

  const donationTicket = isDonationTicketDefinition(ticket)
  const pricingOptionsTicket = hasPricingOptions(ticket)

  let price: string
  if (donationTicket) {
    price = t('tickets.donation')
  } else if (pricingOptionsTicket) {
    if (isPricingOptionsWithSamePrice(ticket)) {
      price = ''
    } else {
      price = getPricingOptionsRangeLabel(ticket, t, getFormattedMoney)
    }
  } else {
    price = getFormattedMoney(ticket.price)
  }

  const onDonationChange = (newDonation: string) => {
    changeTicketDonation({ticketId: ticket.id, donation: newDonation})
  }

  const onDonationBlur = () => {
    const error = checkDonationError(ticket, donation, {excludeErrors: [DONATION_ERROR.EMPTY_DONATION]})
    if (error) {
      setDonationError({ticketId: ticket.id, error})
    }
  }

  return (
    <div className={className}>
      {donationInputVisible ? (
        <Donation
          ticket={ticket}
          value={donation}
          onChange={onDonationChange}
          onBlur={onDonationBlur}
          error={donationError}
        />
      ) : (
        <Price label={t('ticketsThankYou.price')} id={ticket.id} price={price} />
      )}
      {!pricingOptionsTicket ? <Fees ticket={ticket} overridePrice={donation} /> : null}
      <GoesOnSaleLabel ticket={ticket} className={s.goesOnSaleLabel} />
      <OnSaleLabel ticket={ticket} className={s.onSaleLabel} />
    </div>
  )
}
